/**
 * Security utilities for the DeeTrade frontend
 */

/**
 * Generate a device fingerprint from browser characteristics
 * @returns {Object} Device fingerprint data
 */
export const getDeviceFingerprint = () => {
  const screenInfo = {
    width: window.screen.width,
    height: window.screen.height,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
  };

  const browserInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    vendor: navigator.vendor,
    cookiesEnabled: navigator.cookieEnabled,
  };

  const timeZone = {
    zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    offset: new Date().getTimezoneOffset(),
  };

  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl");
  const webGLInfo = gl
    ? {
        vendor: gl.getParameter(gl.VENDOR),
        renderer: gl.getParameter(gl.RENDERER),
      }
    : null;

  return {
    screen: screenInfo,
    browser: browserInfo,
    timeZone,
    webGL: webGLInfo,
    timestamp: new Date().toISOString(),
    isSecureContext: window.isSecureContext,
  };
};

/**
 * Validate a JWT token's expiration
 * @param {string} token JWT token
 * @returns {boolean} Whether the token is expired
 */
/**
 * Decode JWT token payload
 * @param {string} token 
 * @returns {Object|null} Decoded payload
 */
export const decodeJwt = (token) => {
  if (!token) return null;

  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error parsing token:", error);
    return null;
  }
};

/**
 * Validate a JWT token's expiration
 * @param {string} token JWT token
 * @returns {boolean} Whether the token is expired
 */
export const isTokenExpired = (token) => {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

/**
 * Get time remaining for a token in seconds
 * @param {string} token 
 * @returns {number} Seconds remaining
 */
export const getTokenTimeRemaining = (token) => {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) return 0;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const remaining = decoded.exp - currentTime;
  return remaining > 0 ? remaining : 0;
};

/**
 * Sanitize user input to prevent XSS attacks
 * @param {string} input User input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;

  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
};

/**
 * Check if the current context is secure (HTTPS)
 * @returns {boolean} Whether the context is secure
 */
export const isSecureContext = () => {
  return window.isSecureContext && window.location.protocol === "https:";
};

/**
 * Clear all auth-related data from storage
 */
/**
 * Clear all auth-related data from storage
 */
export const clearAuthData = () => {
  localStorage.removeItem("accessToken"); // Fixed key name
  localStorage.removeItem("userProfile");
  sessionStorage.clear();

  // Clear all cookies in the current domain
  document.cookie.split(";").forEach((cookie) => {
    document.cookie = cookie
      .replace(/^ +/, "")
      .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`);
  });
};

/**
 * Synchronized token refresh using Web Locks API to prevent race conditions across tabs/instances
 * @param {Function} refreshCall - Async function that performs the actual API refresh call and returns new token
 * @returns {Promise<string>} The valid access token (either reused or newly refreshed)
 */
export const synchronizedTokenRefresh = async (refreshCall) => {
  // If Web Locks API is not supported (unlikely in modern browsers), fallback to direct call
  if (!navigator.locks) {
     console.warn("Web Locks API not supported, falling back to direct refresh");
     return refreshCall();
  }

  // Request a lock named "deetrade_token_refresh".
  // The lock is exclusive, so only one tab/process can execute this block at a time.
  return navigator.locks.request("deetrade_token_refresh", async () => {
      // 1. Check if token is already valid (refreshed by another tab while we waited)
      const currentToken = localStorage.getItem("accessToken");
      if (currentToken && !isTokenExpired(currentToken)) {
          // Double check if it's "fresh enough" (e.g. > 1 min remaining)
          /*
          const remaining = getTokenTimeRemaining(currentToken);
          if (remaining > 60) {
              console.log("Token already refreshed by another tab/process, reusing.");
              return currentToken;
          }
          */
      }

      // 2. Token needs refresh
      console.log("Acquired lock, executing token refresh...");
      try {
          const newToken = await refreshCall();
          return newToken;
      } catch (e) {
          console.error("Refresh failed inside lock", e);
          throw e; // Propagate error so caller can logout
      }
  });
};
